<template>
    <page-select v-model="model"
        :options="list || []"
        :loading="loading"
        :getOptionKey="(option: Group) => option.group_id"
        data-test="page-select-org-unit-groups">

        <template v-slot:header>
            <slot name="header">Select Groups</slot>
        </template>

        <template v-slot:subheader>
            <slot name="subheader"></slot>
        </template>

        <template v-slot:select-all-right>
            <div class="ml-auto">
                <vx-dropdown v-if="groupCollections.length" placement="right">
                    <vx-button color="muted" :loading="loadingCollectionsMemberships > 0" size="xs">Select by Collection <font-awesome-icon widthAuto :icon="faCaretDown"></font-awesome-icon></vx-button>
                    <template v-slot:items>
                        <vx-dropdown-item v-for="groupCollection in groupCollections" :key="groupCollection.id" @click="selectGroupCollection(groupCollection)" :data-test="`group-collection-${groupCollection.id}`">{{groupCollection.name}}</vx-dropdown-item>
                    </template>
                </vx-dropdown>
            </div>
        </template>

        <template v-slot:option="option">
            <slot name="option"
                v-bind="option">
                <strong> {{ option.item.name }}</strong>
                - {{ option.item.group_id }}
            </slot>
        </template>

    </page-select>
</template>

<script setup lang="ts">
import { VxButton, VxDropdown, VxDropdownItem } from '@voxie/frontend-components';
import { groupCollectionsClient, orgUnitGroupsClient } from '~/services';
import PageSelect from '~/components/general/PageSelect.vue';
import { onMounted, ref, type PropType } from 'vue';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import type { GroupCollection } from '~/services/groupCollections/groupCollectionsClient.types';
import type { Group } from '~/services/groups/groupsClient.types';
import type { AxiosResponse } from 'axios';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const teamId = Spark.state.currentTeam.id;

const model = defineModel({
    type: Array as PropType<Group[]>,
    required: true,
});

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    }
});

const toasted = useToasted();

const loading = ref(false)
const list = ref<Group[]>([])

const loadData = (async () => {
    try {
        loading.value = true;
    const { data }: AxiosResponse<{data:Group[]}> = await orgUnitGroupsClient.listOrgUnitGroups(teamId, props.orgUnitId, {
        minimal: 1,
        per_page: 500,
    },);

    list.value = data.data.filter(group => group.name).map((group) => ({
        ...group,
        value: group.group_id,
        label: `${group.name} - ${group.group_id}`.trim()
    }));
    } catch (e) {
        toasted.error(catchRequestError(e))
    } finally {
        loading.value = false;
    }
});

const groupCollections = ref<GroupCollection[]>([]);
const loadGroupCollections = async () => {
    const response = await groupCollectionsClient.getGroupCollections({
        teamId: teamId,
        orgUnitId: props.orgUnitId,
    })

    groupCollections.value = response.data.data;
}

const collectionsMemberships: Record<number, string[]> = {}

// 0 its not loading, multiple memberships can be clicked and
// we need to track each of the collection that was clicked
const loadingCollectionsMemberships = ref(0);

const selectCollectionMembership = (groupCollectionId: number) => {
    const groupIds = collectionsMemberships[groupCollectionId];

    if (!groupIds) {
        return;
    }

    const groupsToAdd = list.value.filter(group =>
        groupIds.includes(group.group_id) && !model.value.find(selectedGroup => selectedGroup.group_id == group.group_id)
    );


    model.value = [...model.value, ...groupsToAdd];
}

const selectGroupCollection = async (groupCollection: GroupCollection)=>{
    if (collectionsMemberships[groupCollection.id]) {
        selectCollectionMembership(groupCollection.id);
        return;
    }

    loadingCollectionsMemberships.value++;
    collectionsMemberships[groupCollection.id] = [];

    try {
        const response = await groupCollectionsClient.getGroupCollectionsGroupMemberships({
                teamId: teamId,
                orgUnitId:props.orgUnitId,
            }, {
                group_collection_ids: [groupCollection.id]
            })

        loadingCollectionsMemberships.value--;
        collectionsMemberships[groupCollection.id] = response.data.memberships[groupCollection.id] ?? [];
        selectCollectionMembership(groupCollection.id);
    } catch (e) {
        catchRequestError(e);
        toasted.error(`It was not possible to select the groups from the collection "${groupCollection.name}"`)
    }
}

onMounted(() => {
    loadData();
    loadGroupCollections();
})

</script>
